// One inline SVG per product. Thin lines, on-palette, ship in the JS bundle
// so there's no /public/ asset serving to worry about. Strokes use
// currentColor, so you set the color via Tailwind text-* on the wrapper.
//
// Color-code map (2026-08-06 Founder directive, Maxx Mode overhaul):
//   sage        → green     text-sage-accent
//   quill       → violet    text-quill-accent
//   loom        → blue      text-loom-accent
//   baker       → orange    text-baker-accent  (alias: oven)
//   postmaster  → crimson   text-postmaster-accent
//   marquee     → gold      text-marquee-accent
//   herald      → teal      text-herald-accent

const MARKS = {
  // Sage — a single sage leaf with its veins.
  sage: (
    <>
      <path d="M32 6 C 44 12, 52 22, 52 34 C 52 46, 42 56, 30 58 C 18 58, 10 48, 12 34 C 14 20, 22 10, 32 6 Z" />
      <path d="M32 8 L 30 58" strokeLinecap="round" />
      <path d="M32 22 L 22 30" strokeLinecap="round" />
      <path d="M32 22 L 44 30" strokeLinecap="round" />
      <path d="M32 36 L 20 42" strokeLinecap="round" />
      <path d="M32 36 L 46 42" strokeLinecap="round" />
    </>
  ),
  // Quill — feather + nib slice.
  quill: (
    <>
      <path d="M 12 54 L 46 20" strokeLinecap="round" />
      <path d="M 46 20 C 52 14, 54 8, 50 6 C 42 6, 34 10, 26 18 C 18 26, 14 34, 14 42 L 24 42 L 20 48 L 30 48 L 26 54 Z" strokeLinejoin="round" />
      <path d="M 12 54 L 8 58" strokeLinecap="round" />
    </>
  ),
  // Loom — warp + weft crossing, two threads over-under.
  loom: (
    <>
      <path d="M 12 12 L 52 52" strokeLinecap="round" />
      <path d="M 52 12 L 12 52" strokeLinecap="round" />
      <path d="M 8 22 L 56 22" strokeLinecap="round" />
      <path d="M 8 42 L 56 42" strokeLinecap="round" />
      <path d="M 22 8 L 22 56" strokeLinecap="round" />
      <path d="M 42 8 L 42 56" strokeLinecap="round" />
    </>
  ),
  // Herald — trumpet + note flag. Teal, hospitable.
  herald: (
    <>
      <path d="M 10 32 L 34 22 L 34 42 Z" strokeLinejoin="round" />
      <path d="M 34 26 L 50 26 L 54 30 L 54 34 L 50 38 L 34 38" strokeLinejoin="round" />
      <path d="M 54 22 L 54 44" strokeLinecap="round" />
      <path d="M 54 30 L 62 26" strokeLinecap="round" />
    </>
  ),
  // Baker (formerly Oven) — flame inside a forge arch.
  oven: (
    <>
      <path d="M 10 54 L 10 26 C 10 16, 22 10, 32 10 C 42 10, 54 16, 54 26 L 54 54 Z" strokeLinejoin="round" />
      <path d="M 32 46 C 26 46, 22 42, 22 36 C 22 32, 26 30, 28 26 C 28 32, 32 30, 32 24 C 36 28, 42 30, 42 36 C 42 42, 38 46, 32 46 Z" strokeLinejoin="round" />
      <path d="M 6 54 L 58 54" strokeLinecap="round" />
    </>
  ),
  // Postmaster — sealed envelope. Flap open, wax seal centred.
  postmaster: (
    <>
      <path d="M 8 18 L 8 50 L 56 50 L 56 18 Z" strokeLinejoin="round" />
      <path d="M 8 18 L 32 36 L 56 18" strokeLinejoin="round" />
      <circle cx="32" cy="42" r="6" />
      <path d="M 29 39 L 35 45" strokeLinecap="round" />
      <path d="M 35 39 L 29 45" strokeLinecap="round" />
    </>
  ),
  // Marquee — theatre marquee sign with bulb-lit bottom row.
  marquee: (
    <>
      <path d="M 8 16 L 56 16 L 56 40 L 8 40 Z" strokeLinejoin="round" />
      <path d="M 16 40 L 12 50" strokeLinecap="round" />
      <path d="M 48 40 L 52 50" strokeLinecap="round" />
      <circle cx="16" cy="46" r="1.5" />
      <circle cx="24" cy="46" r="1.5" />
      <circle cx="32" cy="46" r="1.5" />
      <circle cx="40" cy="46" r="1.5" />
      <circle cx="48" cy="46" r="1.5" />
      <path d="M 20 24 L 44 24" strokeLinecap="round" />
      <path d="M 20 32 L 44 32" strokeLinecap="round" />
    </>
  ),
};

// VidSmith — film reel with center hub and sprocket holes.
MARKS.vidsmith = (
  <>
    <circle cx="32" cy="32" r="22" />
    <circle cx="32" cy="32" r="4" />
    <circle cx="32" cy="14" r="3" />
    <circle cx="32" cy="50" r="3" />
    <circle cx="14" cy="32" r="3" />
    <circle cx="50" cy="32" r="3" />
    <path d="M 6 32 L 12 32" strokeLinecap="round" />
    <path d="M 52 32 L 58 32" strokeLinecap="round" />
  </>
);

// Brain — folded hemispheres, three arcs a side, connecting stem.
MARKS.brain = (
  <>
    <path d="M 32 12 C 20 12, 12 20, 12 32 C 12 44, 20 52, 32 52 C 44 52, 52 44, 52 32 C 52 20, 44 12, 32 12 Z" strokeLinejoin="round" />
    <path d="M 32 12 L 32 52" strokeLinecap="round" />
    <path d="M 20 20 C 24 22, 26 26, 24 32 C 22 38, 24 42, 20 44" strokeLinecap="round" />
    <path d="M 44 20 C 40 22, 38 26, 40 32 C 42 38, 40 42, 44 44" strokeLinecap="round" />
    <path d="M 32 22 L 26 24" strokeLinecap="round" />
    <path d="M 32 22 L 38 24" strokeLinecap="round" />
    <path d="M 32 40 L 26 42" strokeLinecap="round" />
    <path d="M 32 40 L 38 42" strokeLinecap="round" />
  </>
);

// Purser — small drawstring pouch with cinched top and coin arc.
MARKS.purser = (
  <>
    <path d="M 14 26 C 14 22, 18 20, 22 20 L 42 20 C 46 20, 50 22, 50 26 L 52 52 C 52 56, 48 56, 44 56 L 20 56 C 16 56, 12 56, 12 52 Z" strokeLinejoin="round" />
    <path d="M 22 20 L 22 12 C 22 10, 24 8, 26 8 L 38 8 C 40 8, 42 10, 42 12 L 42 20" strokeLinejoin="round" />
    <circle cx="32" cy="38" r="6" />
    <path d="M 30 36 L 34 40" strokeLinecap="round" />
    <path d="M 34 36 L 30 40" strokeLinecap="round" />
  </>
);

// Mender — shield outline with a stitched-X sutured across it.
MARKS.mender = (
  <>
    <path d="M 32 8 L 52 14 L 52 32 C 52 44, 44 52, 32 58 C 20 52, 12 44, 12 32 L 12 14 Z" strokeLinejoin="round" />
    <path d="M 22 22 L 42 42" strokeLinecap="round" />
    <path d="M 42 22 L 22 42" strokeLinecap="round" />
    <path d="M 24 20 L 26 24" strokeLinecap="round" />
    <path d="M 40 20 L 38 24" strokeLinecap="round" />
    <path d="M 24 44 L 26 40" strokeLinecap="round" />
    <path d="M 40 44 L 38 40" strokeLinecap="round" />
  </>
);

// Choir — five-line music staff with a rising treble curl and two beats.
MARKS.choir = (
  <>
    <path d="M 8 18 L 56 18" strokeLinecap="round" />
    <path d="M 8 26 L 56 26" strokeLinecap="round" />
    <path d="M 8 34 L 56 34" strokeLinecap="round" />
    <path d="M 8 42 L 56 42" strokeLinecap="round" />
    <path d="M 8 50 L 56 50" strokeLinecap="round" />
    <path d="M 20 14 C 24 20, 24 30, 20 38 C 16 44, 22 50, 26 44 C 30 38, 24 32, 20 34" strokeLinejoin="round" />
    <ellipse cx="40" cy="42" rx="5" ry="3" />
    <path d="M 45 42 L 45 20" strokeLinecap="round" />
    <path d="M 45 20 L 52 22" strokeLinecap="round" />
  </>
);

// Beacon — a lighthouse tower with three widening beams at the top.
MARKS.beacon = (
  <>
    <path d="M 24 28 L 24 54 L 40 54 L 40 28 Z" strokeLinejoin="round" />
    <path d="M 22 28 L 42 28" strokeLinecap="round" />
    <path d="M 26 24 L 38 24 L 40 28 L 24 28 Z" strokeLinejoin="round" />
    <circle cx="32" cy="18" r="4" />
    <path d="M 22 10 L 32 14" strokeLinecap="round" />
    <path d="M 42 10 L 32 14" strokeLinecap="round" />
    <path d="M 14 18 L 26 18" strokeLinecap="round" />
    <path d="M 38 18 L 50 18" strokeLinecap="round" />
    <path d="M 20 54 L 44 54" strokeLinecap="round" />
    <path d="M 28 34 L 36 34" strokeLinecap="round" />
    <path d="M 28 42 L 36 42" strokeLinecap="round" />
  </>
);

// Medic — a plus sign inside a soft heart. Care + medicine, one glyph.
MARKS.medic = (
  <>
    <path d="M 32 54 C 12 42, 8 28, 14 20 C 20 12, 30 14, 32 22 C 34 14, 44 12, 50 20 C 56 28, 52 42, 32 54 Z" strokeLinejoin="round" />
    <path d="M 32 24 L 32 40" strokeLinecap="round" />
    <path d="M 24 32 L 40 32" strokeLinecap="round" />
  </>
);

// Compass — compass rose with cardinal points and an arrow needle.
MARKS.compass = (
  <>
    <circle cx="32" cy="32" r="22" />
    <path d="M 32 12 L 34 30 L 32 32 L 30 30 Z" strokeLinejoin="round" />
    <path d="M 32 52 L 30 34 L 32 32 L 34 34 Z" strokeLinejoin="round" />
    <path d="M 12 32 L 30 30 L 32 32 L 30 34 Z" strokeLinejoin="round" />
    <path d="M 52 32 L 34 34 L 32 32 L 34 30 Z" strokeLinejoin="round" />
    <circle cx="32" cy="32" r="2" fill="currentColor" />
  </>
);

// Steward — a wheat sheaf, bound at the middle. Grants + operations.
MARKS.steward = (
  <>
    <path d="M 32 10 L 32 54" strokeLinecap="round" />
    <path d="M 22 14 L 22 40" strokeLinecap="round" />
    <path d="M 42 14 L 42 40" strokeLinecap="round" />
    <path d="M 14 20 L 14 42" strokeLinecap="round" />
    <path d="M 50 20 L 50 42" strokeLinecap="round" />
    <path d="M 32 14 L 26 10" strokeLinecap="round" />
    <path d="M 32 14 L 38 10" strokeLinecap="round" />
    <path d="M 22 18 L 18 14" strokeLinecap="round" />
    <path d="M 42 18 L 46 14" strokeLinecap="round" />
    <rect x="14" y="34" width="36" height="6" strokeLinejoin="round" />
  </>
);

// Almoner — an open palm with a coin above it, the giver's mark.
MARKS.almoner = (
  <>
    <circle cx="32" cy="16" r="7" />
    <path d="M 28 16 L 36 16" strokeLinecap="round" />
    <path d="M 32 12 L 32 20" strokeLinecap="round" />
    <path d="M 16 42 L 16 34 C 16 30, 20 30, 20 34 L 20 40" strokeLinejoin="round" />
    <path d="M 22 40 L 22 30 C 22 26, 26 26, 26 30 L 26 40" strokeLinejoin="round" />
    <path d="M 28 40 L 28 28 C 28 24, 32 24, 32 28 L 32 40" strokeLinejoin="round" />
    <path d="M 34 40 L 34 30 C 34 26, 38 26, 38 30 L 38 40" strokeLinejoin="round" />
    <path d="M 40 40 L 40 34 C 40 30, 44 30, 44 34 L 44 40" strokeLinejoin="round" />
    <path d="M 14 42 C 14 50, 22 56, 32 56 C 42 56, 46 50, 46 42" strokeLinejoin="round" />
  </>
);

// Legal — scales of justice. Two pans hanging from a horizontal beam.
MARKS.legal = (
  <>
    <path d="M 32 10 L 32 54" strokeLinecap="round" />
    <path d="M 20 54 L 44 54" strokeLinecap="round" />
    <path d="M 12 16 L 52 16" strokeLinecap="round" />
    <path d="M 20 16 L 12 30 L 28 30 Z" strokeLinejoin="round" />
    <path d="M 44 16 L 36 30 L 52 30 Z" strokeLinejoin="round" />
    <path d="M 12 30 C 12 34, 16 36, 20 36 C 24 36, 28 34, 28 30" strokeLinejoin="round" />
    <path d="M 36 30 C 36 34, 40 36, 44 36 C 48 36, 52 34, 52 30" strokeLinejoin="round" />
    <circle cx="32" cy="12" r="2" fill="currentColor" />
  </>
);

// Stadium Seat — a folding chair silhouette + a stadium tier edge.
MARKS["stadium-seat"] = (
  <>
    <path d="M 8 50 L 24 50 L 24 30 L 40 30 L 40 50 L 56 50" strokeLinejoin="round" />
    <path d="M 24 30 L 20 20 L 44 20 L 40 30" strokeLinejoin="round" />
    <path d="M 24 50 L 24 56" strokeLinecap="round" />
    <path d="M 40 50 L 40 56" strokeLinecap="round" />
    <path d="M 8 50 L 8 56" strokeLinecap="round" />
    <path d="M 56 50 L 56 56" strokeLinecap="round" />
    <path d="M 32 30 L 32 24" strokeLinecap="round" />
  </>
);

// Whistleblower — an actual whistle: cylindrical body with a mouthpiece
// and the small keyring loop.
MARKS.whistleblower = (
  <>
    <path d="M 8 26 L 42 26 C 48 26, 52 30, 52 36 C 52 42, 48 46, 42 46 L 8 46 L 8 26 Z" strokeLinejoin="round" />
    <path d="M 8 30 L 42 30" strokeLinecap="round" />
    <circle cx="18" cy="36" r="3" />
    <path d="M 8 26 L 4 22" strokeLinecap="round" />
    <path d="M 8 46 L 4 50" strokeLinecap="round" />
    <path d="M 52 32 L 58 30" strokeLinecap="round" />
    <path d="M 52 40 L 58 42" strokeLinecap="round" />
  </>
);

// Aliases — `baker` uses the oven mark until we redesign the flame.
MARKS.baker = MARKS.oven;

export default function ProductMark({ slug, size = 48, className = "" }) {
  const mark = MARKS[slug] || MARKS.sage;
  return (
    <svg
      data-testid={`product-mark-${slug}`}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`inline-block ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      {mark}
    </svg>
  );
}
